import React, { useState, useEffect } from 'react';
import {
  Button,
  Datagrid,
  DateField,
  Filter,
  FunctionField,
  List,
  NumberField,
  SelectInput,
  Show,
  SimpleShowLayout,
  TextField,
  TextInput,
  TopToolbar,
  useNotify,
  useRefresh,
} from 'react-admin';
import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import DialogActions from '@material-ui/core/DialogActions';
import MuiTextField from '@material-ui/core/TextField';
import Chip from '@material-ui/core/Chip';
import { Link } from 'react-router-dom';
import { ADMIN_URL, httpClient } from '../../common/http';

// ─── Status chip ────────────────────────────────────────────────────────────

const STATUS_COLORS: Record<string, string> = {
  pending: '#ff9800',
  auto_approved: '#2196f3',
  approved: '#4caf50',
  declined: '#f44336',
};

const StatusChip = ({ record }: any) => {
  if (!record) {
    return null;
  }
  const color = STATUS_COLORS[record.status] ?? '#9e9e9e';
  return <Chip label={record.status} size="small" style={{ backgroundColor: color, color: '#fff', fontWeight: 600 }} />;
};

// ─── Filter ─────────────────────────────────────────────────────────────────

const ProofFilter = (props: any) => (
  <Filter {...props}>
    <SelectInput
      label="Status"
      source="status||$eq"
      alwaysOn
      allowEmpty
      choices={[
        { id: 'pending', name: 'Pending' },
        { id: 'auto_approved', name: 'Auto Approved' },
        { id: 'approved', name: 'Approved' },
        { id: 'declined', name: 'Declined' },
      ]}
    />
    <TextInput label="Room ID" source="roomId||$eq" alwaysOn />
    <TextInput label="User ID" source="userId||$eq" alwaysOn />
  </Filter>
);

// ─── List ────────────────────────────────────────────────────────────────────

export const ParticipationProofList = (props: any) => (
  <List
    {...props}
    filters={<ProofFilter />}
    filterDefaultValues={{ 'status||$eq': 'pending' }}
    sort={{ field: 'createdAt', order: 'DESC' }}
    bulkActionButtons={false}
  >
    <Datagrid rowClick="show">
      <TextField source="id" />
      <FunctionField
        label="User"
        render={(record: any) =>
          record?.user ? `${record.user.firstname} ${record.user.lastname} (${record.user.email})` : record?.userId
        }
      />
      <TextField source="room.name" label="Room" />
      <NumberField source="submittedResult" label="Submitted Result" />
      <FunctionField label="Status" render={(record: any) => <StatusChip record={record} />} />
      <DateField source="startDate" />
      <DateField source="createdAt" showTime />
    </Datagrid>
  </List>
);

// ─── Approve / Decline buttons ───────────────────────────────────────────────

const ApproveButton = ({ record, onDone }: { record?: any; onDone: () => void }) => {
  const notify = useNotify();
  const refresh = useRefresh();

  const handleApprove = async () => {
    try {
      const response = await fetch(`${ADMIN_URL}/participation-proof/${record.id}/review`, {
        method: 'POST',
        headers: {
          Authorization: 'Bearer ' + localStorage.getItem('token'),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ decision: 'approved' }),
      });
      if (response.ok) {
        notify('Proof approved', 'success');
        refresh();
        onDone();
      } else {
        const err = await response.json().catch(() => ({}));
        notify(err?.message ?? 'Error approving proof', 'error');
      }
    } catch {
      notify('Error approving proof', 'error');
    }
  };

  return <Button label="Approve" onClick={handleApprove} style={{ color: '#4caf50', marginRight: 8 }} />;
};

const DeclineButton = ({ record, onDone }: { record?: any; onDone: () => void }) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const [open, setOpen] = useState(false);
  const [reason, setReason] = useState('');

  const handleDecline = async () => {
    try {
      const body: any = { decision: 'declined' };
      if (reason.trim()) {
        body.declineReason = reason.trim();
      }
      const response = await fetch(`${ADMIN_URL}/participation-proof/${record.id}/review`, {
        method: 'POST',
        headers: {
          Authorization: 'Bearer ' + localStorage.getItem('token'),
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(body),
      });
      if (response.ok) {
        notify('Proof declined', 'success');
        setOpen(false);
        setReason('');
        refresh();
        onDone();
      } else {
        const err = await response.json().catch(() => ({}));
        notify(err?.message ?? 'Error declining proof', 'error');
      }
    } catch {
      notify('Error declining proof', 'error');
    }
  };

  return (
    <>
      <Button label="Decline" onClick={() => setOpen(true)} style={{ color: '#f44336' }} />
      <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Decline Proof</DialogTitle>
        <DialogContent>
          <MuiTextField
            label="Decline reason (optional)"
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            multiline
            rows={3}
            fullWidth
            variant="outlined"
          />
        </DialogContent>
        <DialogActions>
          <Button label="Cancel" onClick={() => setOpen(false)} />
          <Button label="Confirm Decline" onClick={handleDecline} style={{ color: '#f44336' }} />
        </DialogActions>
      </Dialog>
    </>
  );
};

// ─── Show toolbar ────────────────────────────────────────────────────────────

const actionableStatuses = ['pending', 'auto_approved'];

const ProofShowActions = ({ data, basePath, ...rest }: any) => {
  const [, forceUpdate] = useState(0);
  const isActionable = data && actionableStatuses.includes(data.status);

  if (!isActionable) {
    return <TopToolbar />;
  }

  return (
    <TopToolbar>
      <ApproveButton record={data} onDone={() => forceUpdate((n) => n + 1)} />
      <DeclineButton record={data} onDone={() => forceUpdate((n) => n + 1)} />
    </TopToolbar>
  );
};

// ─── Proof image (fetches presigned URL via authenticated client) ─────────────

const ProofImage = ({ record }: any) => {
  const [imgUrl, setImgUrl] = useState<string | null>(null);

  useEffect(() => {
    if (!record?.proofImage || !record?.id) return;
    httpClient(`${ADMIN_URL}/participation-proof/${record.id}/proof-image`).then(({ json }: { json: any }) => setImgUrl(json.url));
  }, [record?.id]);

  if (!record?.proofImage) return <span style={{ color: '#9e9e9e' }}>No image</span>;
  return imgUrl ? <img src={imgUrl} alt="Proof" style={{ maxWidth: 600 }} /> : null;
};

// ─── Show ────────────────────────────────────────────────────────────────────

export const ParticipationProofShow = (props: any) => (
  <Show {...props} actions={<ProofShowActions />}>
    <SimpleShowLayout>
      <TextField source="id" />
      <TextField source="userId" label="User ID" />
      <FunctionField
        label="User"
        render={(record: any) =>
          record?.user ? `${record.user.firstname} ${record.user.lastname} — ${record.user.email} (@${record.user.username})` : '—'
        }
      />
      <TextField source="roomId" label="Room ID" />
      <TextField source="room.name" label="Room Name" />
      <NumberField source="submittedResult" label="Submitted Result (s / m)" />
      <FunctionField label="Status" render={(record: any) => <StatusChip record={record} />} />
      <TextField source="declineReason" label="Decline Reason" />
      <DateField source="startDate" label="Start Date" />
      <DateField source="createdAt" showTime />
      <DateField source="updatedAt" showTime />

      <FunctionField
        label="Participation"
        render={(record: any) =>
          record?.participationId ? (
            <Link to={`/participation/${record.participationId}/show`}>#{record.participationId}</Link>
          ) : (
            <span style={{ color: '#9e9e9e' }}>Not yet assigned</span>
          )
        }
      />

      <FunctionField label="Proof Image" render={(record: any) => <ProofImage record={record} />} />
    </SimpleShowLayout>
  </Show>
);
